import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { CustomToolRunner } from "@/components/ai-documents/custom-tool-runner";
import { Link } from "@/core/lib/navigation";
import { useSession } from "@/core/lib/session-context";
import { companyQuery, documentQuery, profileQuery } from "@/lib/loaders";

/**
 * Runs a template to generate a document. The loader resolves the company (for
 * company-bound fields + the letterhead) and the signed-in user (for
 * `user`-bound fields); the template itself is loaded from the server-backed
 * store by the client runner, which searches employees on demand for
 * employee-bound fields. `?doc=` re-opens a saved document.
 */
export const Route = createFileRoute("/_workspace/generate/custom/$id")({
  validateSearch: (search: Record<string, unknown>) => ({
    doc: typeof search.doc === "string" ? search.doc : undefined,
    view: typeof search.view === "string" ? search.view : undefined,
  }),
  loaderDeps: ({ search }) => ({ doc: search.doc, view: search.view }),
  loader: async ({ context, deps }) => {
    // A generated document belongs on the dedicated `/document/{id}` view; only
    // drafts with no output and the explicit "Edit form" path stay in the runner.
    if (deps.doc && deps.view !== "form") {
      // Best-effort: this lookup only powers the redirect, so a transient
      // backend failure must not break the runner.
      const existing = await context.queryClient
        .ensureQueryData(documentQuery(deps.doc))
        .catch(() => null);
      if (existing?.output) {
        throw redirect({ to: "/document/$id", params: { id: deps.doc } });
      }
    }
    await Promise.all([
      context.queryClient.ensureQueryData(companyQuery),
      context.queryClient.ensureQueryData(profileQuery),
    ]);
  },
  component: RunCustomToolPage,
});

function RunCustomToolPage() {
  const { id } = Route.useParams();
  const { doc, view } = Route.useSearch();
  const { data: company } = useQuery(companyQuery);
  const { data: profile } = useQuery(profileQuery);
  const session = useSession();

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="space-y-3">
          <Link
            href="/generate"
            className="inline-flex items-center gap-1.5 text-muted-foreground text-sm underline-offset-4 hover:underline"
          >
            <ArrowLeftIcon className="size-4" />
            All documents
          </Link>
        </div>
        <CustomToolRunner
          toolId={id}
          company={company ?? null}
          user={{ name: session.user.name, email: session.user.email }}
          seedDocId={doc}
          savedSignature={profile?.signature ?? ""}
          defaultSignerName={session.user.name || company?.issuerName || profile?.displayName || ""}
          forceForm={view === "form"}
        />
      </div>
    </main>
  );
}
